import React, { useState, useEffect, useCallback } from "react";
import { JsonRpc } from "@proton/js";
import { normalizeTimestamp, formatDate } from "../utils/dateUtils";
import { approveMarketTransaction } from "../utils/crypto";
import CommentReactions, {
  ReactionData,
  DEFAULT_REACTIONS,
} from "./CommentReactions";
import Loader from "./loader/Loader";

interface ProposedMarket {
  id: number;
  question: string;
  category: string;
  image_url: string;
  expireSec: number;
  suggested_by: string;
  isLive: boolean;
  resolved: boolean;
  outcomes: string[];
  outcomes_count: number;
  approvalCount: number;
  userHasApproved: boolean;
  total_collateral_in: number;
  q_yes: number;
  q_no: number;
}

interface VoteHistoryRecord {
  voter: string;
  votedAt: string;
}

interface MarketMeta {
  description: string;
  resolution_criteria: string;
}

interface DeclineRecord {
  market_id: number;
  declined_by: string;
  decline_comment: string;
  declined_at: string;
}

interface Comment {
  id: number;
  market_id: number;
  user_account: string;
  comment_text: string;
  created_at: number;
  updated_at: number | null;
  parent_comment_id: number | null;
  is_deleted: number;
  deleted_by: string | null;
  deleted_at: number | null;
}

interface UpcomingMarketsProps {
  session: any;
  initialMarketId?: number | null;
  onBackToList?: () => void;
  onNavigateToDetail?: (marketId: number) => void;
}

const UpcomingMarkets: React.FC<UpcomingMarketsProps> = ({
  session,
  initialMarketId,
  onBackToList,
  onNavigateToDetail,
}) => {
  const [activeTab, setActiveTab] = useState<"all" | "approved">("all");
  const [proposals, setProposals] = useState<ProposedMarket[]>([]);
  const [loading, setLoading] = useState(false);
  const [approveLoadingId, setApproveLoadingId] = useState<number | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);
  // Market detail view state
  const [selectedMarketId, setSelectedMarketId] = useState<number | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailMarket, setDetailMarket] = useState<ProposedMarket | null>(null);
  const [detailVoters, setDetailVoters] = useState<VoteHistoryRecord[]>([]);
  const [detailMeta, setDetailMeta] = useState<MarketMeta | null>(null);

  // Comment state
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentAdmins, setCommentAdmins] = useState<string[]>([]);
  const [newComment, setNewComment] = useState("");
  const [commentLoading, setCommentLoading] = useState(false);
  const [replyingTo, setReplyingTo] = useState<Comment | null>(null);
  const [commentReactions, setCommentReactions] = useState<
    Record<number, Record<string, ReactionData>>
  >({});
  const [userCommentReactions, setUserCommentReactions] = useState<
    Record<number, string | null>
  >({});
  // Card comment previews: market_id -> comments
  const [cardComments, setCardComments] = useState<Record<number, Comment[]>>(
    {},
  );
  // Decline records: market_id -> decline record
  const [declineRecords, setDeclineRecords] = useState<
    Record<number, DeclineRecord>
  >({});
  const [detailDecline, setDetailDecline] = useState<DeclineRecord | null>(
    null,
  );
  // Edit comment state
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editCommentText, setEditCommentText] = useState("");
  const [editCommentLoading, setEditCommentLoading] = useState(false);
  const [xpredHolders, setXpredHolders] = useState<Set<string>>(new Set());

  const showToast = (
    message: string,
    type: "success" | "error" = "success",
  ) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchApprovalCount = useCallback(
    async (
      rpc: JsonRpc,
      marketId: number,
    ): Promise<{ count: number; userApproved: boolean }> => {
      try {
        const contractName =
          process.env.REACT_APP_CONTRACT_NAME || "xpredict";
        const currentUser = session?.auth?.actor?.toString() || "";
        const result = await rpc.get_table_rows({
          json: true,
          code: contractName,
          scope: marketId.toString(),
          table: "mktintrst",
          limit: 100,
        });
        let count = 0;
        let userApproved = false;
        for (const row of result.rows) {
          count++;
          if (row.voter === currentUser) {
            userApproved = true;
          }
        }
        return { count, userApproved };
      } catch (error) {
        console.error(
          `Error fetching approvals for market ${marketId}:`,
          error,
        );
        return { count: 0, userApproved: false };
      }
    },
    [session],
  );

  const fetchDeclineRecords = useCallback(
    async (rpc: JsonRpc): Promise<Record<number, DeclineRecord>> => {
      try {
        const contractName =
          process.env.REACT_APP_CONTRACT_NAME || "xpredict";
        const result = await rpc.get_table_rows({
          json: true,
          code: contractName,
          scope: contractName,
          table: "mktdecline",
          limit: 1000,
        });
        const records: Record<number, DeclineRecord> = {};
        for (const row of result.rows) {
          records[row.market_id] = row;
        }
        return records;
      } catch (error) {
        console.error("Error fetching decline records:", error);
        return {};
      }
    },
    [],
  );

  const fetchApprovalRecords = useCallback(
    async (rpc: JsonRpc, marketId: number): Promise<VoteHistoryRecord[]> => {
      try {
        const contractName =
          process.env.REACT_APP_CONTRACT_NAME || "xpredict";
        const result = await rpc.get_table_rows({
          json: true,
          code: contractName,
          scope: marketId.toString(),
          table: "mktintrst",
          limit: 100,
        });
        return result.rows.map((row: any) => ({
          voter: row.voter || "",
          votedAt: row.epoch_time || "",
        }));
      } catch (error) {
        console.error(
          `Error fetching approval records for market ${marketId}:`,
          error,
        );
        return [];
      }
    },
    [],
  );

  const fetchProposals = useCallback(async () => {
    if (!session) return;

    setLoading(true);
    try {
      const rpc = new JsonRpc(
        process.env.REACT_APP_RPC_ENDPOINT ||
          process.env.REACT_APP_PROTON_ENDPOINT ||
          "https://proton.eosusa.io",
      );
      const contractName = process.env.REACT_APP_CONTRACT_NAME || "xpredict";

      const result = await rpc.get_table_rows({
        json: true,
        code: contractName,
        scope: contractName,
        table: "markets3",
        limit: 1000,
      });

      const nowSec = Math.floor(Date.now() / 1000);

      // Filter: not expired AND not live (pending approval only)
      const pendingMarkets = result.rows.filter((row: any) => {
        const expireSec = normalizeTimestamp(row.expire);
        const isExpired = expireSec > 0 && expireSec < nowSec;
        const isLive = row.isLive || false;
        const isResolved = row.resolved || false;
        const hasVisibleStatus = row.status === 1;
        return !isExpired && !isLive && !isResolved && hasVisibleStatus;
      });

      // Fetch approval counts for each market
      const proposalsWithApprovals: ProposedMarket[] = [];
      for (const row of pendingMarkets) {
        const { count, userApproved } = await fetchApprovalCount(rpc, row.id);
        proposalsWithApprovals.push({
          id: row.id,
          question: row.question,
          category: row.category,
          image_url: row.image_url || "",
          expireSec: normalizeTimestamp(row.expire),
          suggested_by: row.suggested_by || "",
          isLive: row.isLive || false,
          resolved: row.resolved || false,
          outcomes: row.outcomes
            ? typeof row.outcomes === "string"
              ? row.outcomes.split(",")
              : row.outcomes
            : [],
          outcomes_count: row.outcomes_count || 2,
          approvalCount: count,
          userHasApproved: userApproved,
          total_collateral_in: row.total_collateral_in || 0,
          q_yes: row.q_yes || 0,
          q_no: row.q_no || 0,
        });
      }

      setProposals(proposalsWithApprovals);

      // Fetch decline records for all markets
      const declines = await fetchDeclineRecords(rpc);
      setDeclineRecords(declines);
    } catch (error) {
      console.error("Error fetching proposals:", error);
      showToast("Failed to load proposals", "error");
    } finally {
      setLoading(false);
    }
  }, [session, fetchApprovalCount, fetchDeclineRecords]);

  useEffect(() => {
    fetchProposals();
  }, [fetchProposals]);

  // Auto-open detail view when initialMarketId prop is provided (e.g. from notification link)
  useEffect(() => {
    if (initialMarketId != null) {
      handleViewDetail(initialMarketId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMarketId]);

  const handleApprove = async (marketId: number) => {
    if (!session) return;

    setApproveLoadingId(marketId);
    try {
      await approveMarketTransaction(session, {
        voter: session.auth.actor,
        market_id: marketId,
      });

      showToast("Vote recorded successfully!");

      // Update the local state immediately
      setProposals((prev) =>
        prev.map((p) => {
          if (p.id === marketId) {
            return {
              ...p,
              approvalCount: p.approvalCount + 1,
              userHasApproved: true,
            };
          }
          return p;
        }),
      );

      // Also update the detail view if it's open for this market
      if (selectedMarketId === marketId && detailMarket) {
        setDetailMarket({
          ...detailMarket,
          approvalCount: detailMarket.approvalCount + 1,
          userHasApproved: true,
        });
        setDetailVoters((prev) => [
          ...prev,
          {
            voter: session.auth.actor.toString(),
            votedAt: new Date().toISOString().replace("Z", ""),
          },
        ]);
      }

      // Refresh data from chain after a short delay
      setTimeout(() => {
        fetchProposals();
        if (selectedMarketId === marketId) {
          fetchMarketDetail(marketId);
        }
      }, 2000);
    } catch (error: any) {
      console.error("Error approving market:", error);
      const errorMsg = error?.message || error?.toString() || "Unknown error";
      showToast("Failed to vote: " + errorMsg, "error");
    } finally {
      setApproveLoadingId(null);
    }
  };

  const fetchMarketDetail = useCallback(
    async (marketId: number) => {
      setDetailLoading(true);
      try {
        const rpc = new JsonRpc(
          process.env.REACT_APP_RPC_ENDPOINT ||
            process.env.REACT_APP_PROTON_ENDPOINT ||
            "https://proton.eosusa.io",
        );

        // Find the proposal in existing state
        const existing = proposals.find((p) => p.id === marketId);
        if (existing) {
          setDetailMarket(existing);
        } else {
          // Fetch from chain if not in proposals list
          const contractName =
            process.env.REACT_APP_CONTRACT_NAME || "xpredict";
          const result = await rpc.get_table_rows({
            json: true,
            code: contractName,
            scope: contractName,
            table: "markets3",
            lower_bound: marketId,
            upper_bound: marketId,
            limit: 1,
          });
          if (result.rows.length > 0) {
            const row = result.rows[0];
            const { count, userApproved } = await fetchApprovalCount(
              rpc,
              row.id,
            );
            setDetailMarket({
              id: row.id,
              question: row.question,
              category: row.category,
              image_url: row.image_url || "",
              expireSec: normalizeTimestamp(row.expire),
              suggested_by: row.suggested_by || "",
              isLive: row.isLive || false,
              resolved: row.resolved || false,
              outcomes: row.outcomes
                ? typeof row.outcomes === "string"
                  ? row.outcomes.split(",")
                  : row.outcomes
                : [],
              outcomes_count: row.outcomes_count || 2,
              approvalCount: count,
              userHasApproved: userApproved,
              total_collateral_in: row.total_collateral_in || 0,
              q_yes: row.q_yes || 0,
              q_no: row.q_no || 0,
            });
          }
        }

        // Fetch all approval records (voters)
        const voters = await fetchApprovalRecords(rpc, marketId);
        setDetailVoters(voters);

        // Fetch decline record for this market
        try {
          const contractName =
            process.env.REACT_APP_CONTRACT_NAME || "xpredict";
          const declineResult = await rpc.get_table_rows({
            json: true,
            code: contractName,
            scope: contractName,
            table: "mktdecline",
            lower_bound: marketId,
            upper_bound: marketId,
            limit: 1,
          });
          if (
            declineResult.rows.length > 0 &&
            declineResult.rows[0].market_id === marketId
          ) {
            setDetailDecline(declineResult.rows[0]);
          } else {
            setDetailDecline(null);
          }
        } catch {
          setDetailDecline(null);
        }

        // Fetch market meta (description, resolution criteria)
        try {
          const response = await fetch(
            `/api/market_meta.php?market_id=${marketId}`,
          );
          const data = await response.json();
          if (data.success && data.data) {
            setDetailMeta({
              description: data.data.description || "",
              resolution_criteria: data.data.resolution_criteria || "",
            });
          } else {
            setDetailMeta(null);
          }
        } catch {
          setDetailMeta(null);
        }
      } catch (error) {
        console.error("Error fetching market detail:", error);
        showToast("Failed to load market details", "error");
      } finally {
        setDetailLoading(false);
      }
    },
    [proposals, fetchApprovalCount, fetchApprovalRecords],
  );

  // ===== Comment functions =====
  const fetchComments = useCallback(async (marketId: number) => {
    try {
      const response = await fetch(`/api/comments.php?market_id=${marketId}`);
      const data = await response.json();
      if (data.success) {
        setComments(data.comments || []);
        if (data.admins) {
          setCommentAdmins(data.admins);
        }
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
    }
  }, []);

  const fetchXpredHolderStatus = useCallback(async (accounts: string[]) => {
    if (accounts.length === 0) return;
    try {
      const rpc = new JsonRpc(
        process.env.REACT_APP_PROTON_ENDPOINT || "https://proton.eosusa.io",
      );
      const xpredContract =
        process.env.REACT_APP_XPRED_CONTRACT || "tokencreate";
      const holders = new Set<string>();
      for (const account of accounts) {
        try {
          const result = await rpc.get_currency_balance(
            xpredContract,
            account,
            "XPRED",
          );
          if (result && result.length > 0) {
            const balance = parseFloat(result[0].split(" ")[0]);
            if (balance > 0) holders.add(account);
          }
        } catch {
          // skip individual account errors
        }
      }
      setXpredHolders(holders);
    } catch (error) {
      console.error("Error fetching XPRED holder status:", error);
    }
  }, []);

  const fetchCommentReactionsData = useCallback(
    async (marketId: number) => {
      try {
        const userAccount = session?.auth?.actor?.toString() || "";
        const url = `/api/reactions.php?market_id=${marketId}${userAccount ? `&user_account=${userAccount}` : ""}`;
        const response = await fetch(url);
        const data = await response.json();
        if (data.success) {
          setCommentReactions(data.reactions_by_comment || {});
          setUserCommentReactions(data.user_reactions || {});
        }
      } catch (error) {
        console.error("Error fetching comment reactions:", error);
      }
    },
    [session],
  );

  const fetchCardComments = useCallback(async (marketIds: number[]) => {
    const newCardComments: Record<number, Comment[]> = {};
    for (const id of marketIds) {
      try {
        const response = await fetch(`/api/comments.php?market_id=${id}`);
        const data = await response.json();
        if (data.success) {
          newCardComments[id] = data.comments || [];
        }
      } catch {
        newCardComments[id] = [];
      }
    }
    setCardComments(newCardComments);
  }, []);

  // Fetch card comment previews when proposals change
  useEffect(() => {
    if (proposals.length > 0) {
      fetchCardComments(proposals.map((p) => p.id));
    }
  }, [proposals, fetchCardComments]);

  // Fetch XPRED holder status for comment authors
  useEffect(() => {
    if (comments.length > 0) {
      const uniqueAuthors = Array.from(
        new Set(comments.map((c) => c.user_account)),
      );
      fetchXpredHolderStatus(uniqueAuthors);
    }
  }, [comments, fetchXpredHolderStatus]);

  const handlePostComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!session || !selectedMarketId) return;

    if (!newComment.trim()) {
      showToast("Please enter a comment", "error");
      return;
    }

    if (newComment.length > 500) {
      showToast("Comment is too long (max 500 characters)", "error");
      return;
    }

    setCommentLoading(true);
    try {
      const response = await fetch("/api/comments.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          market_id: selectedMarketId,
          user_account: session.auth.actor.toString(),
          comment_text: newComment.trim(),
          parent_comment_id: replyingTo ? replyingTo.id : null,
          market_creator: detailMarket?.suggested_by || "",
        }),
      });

      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        showToast("Failed to post comment: Server error", "error");
        return;
      }

      if (data.success) {
        setNewComment("");
        setReplyingTo(null);
        fetchComments(selectedMarketId);
        showToast("Comment posted!");
      } else {
        showToast(
          "Failed to post comment: " + (data.error || "Unknown error"),
          "error",
        );
      }
    } catch (error) {
      console.error("Error posting comment:", error);
      showToast("Failed to post comment", "error");
    } finally {
      setCommentLoading(false);
    }
  };

  const handleReply = (comment: Comment) => {
    if (!session) {
      showToast("Please connect your wallet to reply", "error");
      return;
    }
    setReplyingTo(comment);
    setNewComment("");
    setTimeout(() => {
      const textarea = document.querySelector(
        ".community-comment-form textarea",
      ) as HTMLTextAreaElement;
      if (textarea) {
        textarea.focus();
      }
    }, 100);
  };

  const handleEditComment = async (commentId: number) => {
    if (!session || !selectedMarketId) return;

    const trimmed = editCommentText.trim();
    if (!trimmed) {
      showToast("Comment text is required", "error");
      return;
    }
    if (trimmed.length > 500) {
      showToast("Comment is too long (max 500 characters)", "error");
      return;
    }

    setEditCommentLoading(true);
    try {
      const response = await fetch("/api/comments.php", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          comment_id: commentId,
          user_account: session.auth.actor.toString(),
          comment_text: trimmed,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setEditingCommentId(null);
        setEditCommentText("");
        fetchComments(selectedMarketId);
        showToast("Comment updated!");
      } else {
        showToast(
          "Failed to edit comment: " + (data.error || "Unknown error"),
          "error",
        );
      }
    } catch (error) {
      console.error("Error editing comment:", error);
      showToast("Failed to edit comment", "error");
    } finally {
      setEditCommentLoading(false);
    }
  };

  const startEditComment = (comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditCommentText(comment.comment_text);
  };

  const cancelEditComment = () => {
    setEditingCommentId(null);
    setEditCommentText("");
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!session) return;
    const userAccount = session.auth.actor.toString();
    if (!commentAdmins.includes(userAccount)) {
      showToast("You do not have permission to delete comments", "error");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this comment?"))
      return;

    try {
      const response = await fetch("/api/comments.php", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          comment_id: commentId,
          user_account: userAccount,
        }),
      });
      const data = await response.json();
      if (data.success && selectedMarketId) {
        fetchComments(selectedMarketId);
      } else {
        showToast("Failed to delete comment", "error");
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
      showToast("Failed to delete comment", "error");
    }
  };

  const renderCommentText = (comment: Comment) => {
    if (comment.is_deleted) {
      return (
        <div className="comment-text deleted-text">[Moderator Deleted]</div>
      );
    }
    const lines = comment.comment_text.split("\n");
    if (
      comment.parent_comment_id &&
      lines.length > 0 &&
      lines[0].startsWith("@")
    ) {
      const quotedAuthor = lines[0];
      const quotedText = lines.slice(1).join("\n").trim();
      const replyText = quotedText.split("\n\n").slice(1).join("\n\n");
      const quote = quotedText.split("\n\n")[0];
      return (
        <div className="comment-text">
          {quote && (
            <div className="quoted-message">
              <div className="quote-author">{quotedAuthor}</div>
              <div className="quote-text">{quote}</div>
            </div>
          )}
          {replyText && <div className="reply-text">{replyText}</div>}
        </div>
      );
    }
    return <div className="comment-text">{comment.comment_text}</div>;
  };

  const handleViewDetail = (marketId: number) => {
    if (onNavigateToDetail) {
      onNavigateToDetail(marketId);
      return;
    }
    setSelectedMarketId(marketId);
    fetchMarketDetail(marketId);
    fetchComments(marketId);
    fetchCommentReactionsData(marketId);
  };

  const handleBackToList = () => {
    if (onBackToList) {
      onBackToList();
      return;
    }
    setSelectedMarketId(null);
    setDetailMarket(null);
    setDetailVoters([]);
    setDetailMeta(null);
    setComments([]);
    setNewComment("");
    setReplyingTo(null);
  };

  const filteredProposals =
    activeTab === "approved"
      ? proposals.filter((p) => p.userHasApproved)
      : proposals;

  const currentUser = session?.auth?.actor?.toString() || "";

  // ===== Market Detail View =====
  if (selectedMarketId !== null) {
    const market = detailMarket;

    return (
      <div className="admin-panel">
        <button className="community-back-btn" onClick={handleBackToList}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            width="16"
            height="16"
          >
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to Upcoming Markets
        </button>

        {detailLoading && !market ? (
          <div className="admin-section">
            <Loader
              label="Loading market details..."
              className="app-loader-compact"
            />
          </div>
        ) : market ? (
          <div className="community-detail">
            <div className="community-detail-header">
              <div className="community-detail-title-row">
                <span className="proposal-id">#{market.id}</span>
                <span className="community-vote-count">
                  {market.approvalCount} votes
                </span>
              </div>
              <h2 className="community-detail-question">{market.question}</h2>
            </div>

            {market.image_url && (
              <div className="community-detail-image">
                <img src={market.image_url} alt="Market" />
              </div>
            )}

            <div className="community-detail-info">
              <div className="community-detail-grid">
                <div className="community-detail-field">
                  <span className="community-detail-label">Category</span>
                  <span className="community-detail-value">
                    {market.category}
                  </span>
                </div>
                <div className="community-detail-field">
                  <span className="community-detail-label">Expires</span>
                  <span className="community-detail-value">
                    {formatDate(market.expireSec, true)}
                  </span>
                </div>
                <div className="community-detail-field">
                  <span className="community-detail-label">Proposed by</span>
                  <span className="community-detail-value">
                    {market.suggested_by}
                  </span>
                </div>
                <div className="community-detail-field">
                  <span className="community-detail-label">Status</span>
                  <span className="community-detail-value">
                    {market.isLive
                      ? "Live"
                      : market.resolved
                        ? "Resolved"
                        : "Pending Approval"}
                  </span>
                </div>
                <div className="community-detail-field">
                  <span className="community-detail-label">Market Type</span>
                  <span className="community-detail-value">
                    {market.outcomes_count === 2
                      ? "Binary (Yes/No)"
                      : `Multi-Outcome (${market.outcomes_count} outcomes)`}
                  </span>
                </div>
                <div className="community-detail-field">
                  <span className="community-detail-label">
                    Community Votes
                  </span>
                  <span className="community-detail-value">
                    {market.approvalCount} votes
                  </span>
                </div>
              </div>

              {/* Outcomes Section */}
              {market.outcomes && market.outcomes.length > 0 && (
                <div className="community-detail-section">
                  <h4>Outcomes ({market.outcomes.length})</h4>
                  <div className="community-detail-outcomes-list">
                    {market.outcomes.map((outcome, idx) => (
                      <div key={idx} className="community-detail-outcome-item">
                        <span className="community-detail-outcome-number">
                          {idx + 1}
                        </span>
                        <span className="community-detail-outcome-name">
                          {outcome}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Description Section */}
              <div className="community-detail-section">
                <h4>Description</h4>
                {detailMeta?.description ? (
                  <p className="community-detail-text">
                    {detailMeta.description}
                  </p>
                ) : (
                  <p className="community-detail-text community-detail-empty">
                    No description provided
                  </p>
                )}
              </div>

              {/* Resolution Criteria Section */}
              <div className="community-detail-section">
                <h4>Resolution Criteria</h4>
                {detailMeta?.resolution_criteria ? (
                  <p className="community-detail-text">
                    {detailMeta.resolution_criteria}
                  </p>
                ) : (
                  <p className="community-detail-text community-detail-empty">
                    No resolution criteria provided
                  </p>
                )}
              </div>

              {/* Decline Record Section */}
              {detailDecline && (
                <div
                  className="community-detail-section"
                  style={{
                    background: "rgba(239, 68, 68, 0.08)",
                    border: "1px solid rgba(239, 68, 68, 0.3)",
                    borderRadius: "8px",
                    padding: "12px 16px",
                    marginTop: "12px",
                  }}
                >
                  <h4
                    style={{
                      color: "#ef4444",
                      margin: "0 0 8px 0",
                      fontSize: "14px",
                    }}
                  >
                    Market Declined by Super Admin
                  </h4>
                  <p
                    style={{
                      color: "#fca5a5",
                      margin: "0 0 4px 0",
                      fontSize: "13px",
                    }}
                  >
                    <strong>Declined by:</strong> {detailDecline.declined_by}
                  </p>
                  <p
                    style={{
                      color: "#e2e8f0",
                      margin: "0 0 4px 0",
                      fontSize: "13px",
                    }}
                  >
                    <strong>Reason:</strong> {detailDecline.decline_comment}
                  </p>
                  <p
                    style={{ color: "#94a3b8", margin: "0", fontSize: "12px" }}
                  >
                    {detailDecline.declined_at
                      ? formatDate(
                          normalizeTimestamp(detailDecline.declined_at),
                          true,
                        )
                      : ""}
                  </p>
                </div>
              )}
            </div>

            {/* Vote action section */}
            <div className="community-detail-vote-section">
              <h4>Your Vote</h4>
              {!session ? (
                <div className="community-vote-status own-market">
                  Connect your wallet to vote
                </div>
              ) : market.userHasApproved ? (
                <div className="community-vote-status approved">
                  Awesome! You have already voted for this market.
                </div>
              ) : market.suggested_by === currentUser ? (
                <div className="community-vote-status own-market">
                  You cannot vote on your own proposal
                </div>
              ) : (
                <button
                  className="community-approve-btn"
                  onClick={() => handleApprove(market.id)}
                  disabled={approveLoadingId === market.id}
                >
                  {approveLoadingId === market.id ? (
                    "Voting..."
                  ) : (
                    <>
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        width="16"
                        height="16"
                      >
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                      Vote
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Voters list */}
            <div className="community-detail-voters">
              <h4>Vote History ({detailVoters.length})</h4>
              {detailVoters.length === 0 ? (
                <p className="community-detail-no-voters">No votes yet.</p>
              ) : (
                <div className="community-voters-list">
                  {detailVoters.map((voter, idx) => {
                    const isCurrentUser = voter.voter === currentUser;

                    return (
                      <div
                        key={`${voter.voter}-${voter.votedAt}-${idx}`}
                        className={`community-voter-row${isCurrentUser ? " community-voter-row-current" : ""}`}
                        data-testid="community-voter-row"
                      >
                        <div className="community-voter-main">
                          <span className="community-voter-name">
                            {voter.voter}
                          </span>
                          <span className="community-voter-time">
                            {voter.votedAt
                              ? formatDate(
                                  normalizeTimestamp(voter.votedAt),
                                  true,
                                )
                              : "—"}
                          </span>
                        </div>
                        {isCurrentUser && (
                          <span
                            className="community-voter-current-badge"
                            aria-label="Current user voted"
                          >
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              width="16"
                              height="16"
                            >
                              <path d="M20 6L9 17l-5-5" />
                            </svg>
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Comment Section */}
            <div className="community-comments-section">
              <h4>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  width="18"
                  height="18"
                >
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                </svg>
                Discussion ({comments.filter((c) => !c.is_deleted).length})
              </h4>

              {/* Comment form */}
              {session ? (
                <form
                  onSubmit={handlePostComment}
                  className="community-comment-form"
                >
                  {replyingTo && (
                    <div className="community-replying-banner">
                      <span>
                        Replying to <strong>{replyingTo.user_account}</strong>
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setReplyingTo(null);
                          setNewComment("");
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Share your thoughts on this proposal..."
                    maxLength={500}
                    rows={3}
                    disabled={commentLoading}
                  />
                  <div className="community-comment-form-footer">
                    <span className="community-char-count">
                      {newComment.length}/500
                    </span>
                    <button
                      type="submit"
                      disabled={commentLoading || !newComment.trim()}
                    >
                      {commentLoading ? "Posting..." : "Post Comment"}
                    </button>
                  </div>
                </form>
              ) : (
                <p className="community-login-prompt">
                  Connect your wallet to join the discussion
                </p>
              )}

              {/* Comments list - newest first */}
              <div className="community-comments-list">
                {comments.filter((c) => !c.parent_comment_id).length === 0 ? (
                  <p className="community-no-comments">
                    No comments yet. Be the first to share your thoughts!
                  </p>
                ) : (
                  comments
                    .filter((c) => !c.parent_comment_id)
                    .sort((a, b) => b.created_at - a.created_at)
                    .map((comment) => (
                      <div
                        key={comment.id}
                        className="community-comment-thread"
                      >
                        <div
                          className={`community-comment-card ${comment.is_deleted ? "deleted" : ""}`}
                        >
                          <div className="community-comment-header">
                            <span className="community-comment-author">
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                width="14"
                                height="14"
                              >
                                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                                <circle cx="12" cy="7" r="4" />
                              </svg>
                              {comment.user_account}
                              {comment.user_account === market.suggested_by && (
                                <span className="community-comment-creator-badge">
                                  Creator
                                </span>
                              )}
                              {xpredHolders.has(comment.user_account) && (
                                <span className="community-comment-xpred-badge">
                                  XPRED Holder
                                </span>
                              )}
                            </span>
                            <span className="community-comment-time">
                              {new Date(
                                comment.created_at * 1000,
                              ).toLocaleString()}
                              {comment.updated_at && (
                                <span className="community-comment-edited">
                                  (edited)
                                </span>
                              )}
                            </span>
                          </div>
                          {editingCommentId === comment.id ? (
                            <div className="community-edit-comment-form">
                              <textarea
                                value={editCommentText}
                                onChange={(e) =>
                                  setEditCommentText(e.target.value)
                                }
                                maxLength={500}
                                rows={3}
                                disabled={editCommentLoading}
                              />
                              <div className="community-edit-comment-actions">
                                <span className="community-char-count">
                                  {editCommentText.length}/500
                                </span>
                                <button
                                  onClick={cancelEditComment}
                                  disabled={editCommentLoading}
                                  className="community-edit-cancel-btn"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={() => handleEditComment(comment.id)}
                                  disabled={
                                    editCommentLoading ||
                                    !editCommentText.trim()
                                  }
                                  className="community-edit-save-btn"
                                >
                                  {editCommentLoading ? "Saving..." : "Save"}
                                </button>
                              </div>
                            </div>
                          ) : (
                            renderCommentText(comment)
                          )}
                          {!comment.is_deleted &&
                            editingCommentId !== comment.id && (
                              <div className="community-comment-actions">
                                <button
                                  onClick={() => handleReply(comment)}
                                  className="community-reply-btn"
                                >
                                  <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    width="12"
                                    height="12"
                                  >
                                    <path d="M3 10l7-7v4c11 0 11 9 11 9s-2-5-11-5v4L3 10z" />
                                  </svg>
                                  Reply
                                </button>
                                {session &&
                                  comment.user_account ===
                                    session.auth.actor.toString() && (
                                    <button
                                      onClick={() => startEditComment(comment)}
                                      className="community-edit-btn"
                                    >
                                      Edit
                                    </button>
                                  )}
                                {session &&
                                  commentAdmins.includes(
                                    session.auth.actor.toString(),
                                  ) && (
                                    <button
                                      onClick={() =>
                                        handleDeleteComment(comment.id)
                                      }
                                      className="community-delete-btn"
                                    >
                                      Delete
                                    </button>
                                  )}
                              </div>
                            )}
                          <CommentReactions
                            commentId={comment.id}
                            marketId={market.id}
                            session={session}
                            reactions={
                              commentReactions[comment.id] || DEFAULT_REACTIONS
                            }
                            userReaction={
                              userCommentReactions[comment.id] || null
                            }
                            onReactionChange={() =>
                              fetchCommentReactionsData(market.id)
                            }
                          />
                        </div>

                        {/* Nested replies */}
                        {comments
                          .filter((r) => r.parent_comment_id === comment.id)
                          .sort((a, b) => a.created_at - b.created_at)
                          .map((reply) => (
                            <div
                              key={reply.id}
                              className={`community-comment-card community-comment-reply ${reply.is_deleted ? "deleted" : ""}`}
                            >
                              <div className="community-comment-header">
                                <span className="community-comment-author">
                                  <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    width="14"
                                    height="14"
                                  >
                                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                                    <circle cx="12" cy="7" r="4" />
                                  </svg>
                                  {reply.user_account}
                                  {reply.user_account ===
                                    market.suggested_by && (
                                    <span className="community-comment-creator-badge">
                                      Creator
                                    </span>
                                  )}
                                  {xpredHolders.has(reply.user_account) && (
                                    <span className="community-comment-xpred-badge">
                                      XPRED Holder
                                    </span>
                                  )}
                                </span>
                                <span className="community-comment-time">
                                  {new Date(
                                    reply.created_at * 1000,
                                  ).toLocaleString()}
                                  {reply.updated_at && (
                                    <span className="community-comment-edited">
                                      (edited)
                                    </span>
                                  )}
                                </span>
                              </div>
                              {editingCommentId === reply.id ? (
                                <div className="community-edit-comment-form">
                                  <textarea
                                    value={editCommentText}
                                    onChange={(e) =>
                                      setEditCommentText(e.target.value)
                                    }
                                    maxLength={500}
                                    rows={3}
                                    disabled={editCommentLoading}
                                  />
                                  <div className="community-edit-comment-actions">
                                    <span className="community-char-count">
                                      {editCommentText.length}/500
                                    </span>
                                    <button
                                      onClick={cancelEditComment}
                                      disabled={editCommentLoading}
                                      className="community-edit-cancel-btn"
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleEditComment(reply.id)
                                      }
                                      disabled={
                                        editCommentLoading ||
                                        !editCommentText.trim()
                                      }
                                      className="community-edit-save-btn"
                                    >
                                      {editCommentLoading
                                        ? "Saving..."
                                        : "Save"}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                renderCommentText(reply)
                              )}
                              {!reply.is_deleted &&
                                editingCommentId !== reply.id && (
                                  <div className="community-comment-actions">
                                    {session &&
                                      reply.user_account ===
                                        session.auth.actor.toString() && (
                                        <button
                                          onClick={() =>
                                            startEditComment(reply)
                                          }
                                          className="community-edit-btn"
                                        >
                                          Edit
                                        </button>
                                      )}
                                    {session &&
                                      commentAdmins.includes(
                                        session.auth.actor.toString(),
                                      ) && (
                                        <button
                                          onClick={() =>
                                            handleDeleteComment(reply.id)
                                          }
                                          className="community-delete-btn"
                                        >
                                          Delete
                                        </button>
                                      )}
                                  </div>
                                )}
                              <CommentReactions
                                commentId={reply.id}
                                marketId={market.id}
                                session={session}
                                reactions={
                                  commentReactions[reply.id] ||
                                  DEFAULT_REACTIONS
                                }
                                userReaction={
                                  userCommentReactions[reply.id] || null
                                }
                                onReactionChange={() =>
                                  fetchCommentReactionsData(market.id)
                                }
                              />
                            </div>
                          ))}
                      </div>
                    ))
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="admin-section">
            <p
              style={{
                color: "#94a3b8",
                textAlign: "center",
                padding: "2rem 0",
              }}
            >
              Market not found.
            </p>
          </div>
        )}

        {/* Toast notification */}
        {toast && (
          <div className={`toast-notification ${toast.type}`}>
            <span className="toast-message">{toast.message}</span>
            <button className="toast-close" onClick={() => setToast(null)}>
              &times;
            </button>
          </div>
        )}
      </div>
    );
  }

  // ===== List View =====
  return (
    <div className="admin-panel">
      <h2>Upcoming Markets</h2>
      <p className="admin-subtitle">
        {session
          ? "You can comment and vote on upcoming markets to express your interest."
          : "Users can review and vote on upcoming prediction markets to express their interest."}
      </p>

      <div className="admin-tabs">
        <button
          className={activeTab === "all" ? "active" : ""}
          onClick={() => setActiveTab("all")}
        >
          All Proposed Markets
        </button>
        <button
          className={activeTab === "approved" ? "active" : ""}
          onClick={() => setActiveTab("approved")}
        >
          Voted by Me
        </button>
      </div>

      {loading ? (
        <div className="admin-section">
          <Loader label="Loading proposals..." className="app-loader-compact" />
        </div>
      ) : filteredProposals.length === 0 ? (
        <div className="admin-section">
          <p
            style={{ color: "#94a3b8", textAlign: "center", padding: "2rem 0" }}
          >
            {activeTab === "approved"
              ? "You have not voted on any proposals yet."
              : "No pending proposals at this time."}
          </p>
        </div>
      ) : (
        <div className="community-proposals-list">
          {filteredProposals.map((proposal) => (
            <div
              key={proposal.id}
              className="community-proposal-card community-proposal-card-clickable"
              onClick={() => handleViewDetail(proposal.id)}
            >
              <div className="community-proposal-header">
                <span className="proposal-id">#{proposal.id}</span>
                {(() => {
                  const nowSec = Math.floor(Date.now() / 1000);
                  const isExpired =
                    proposal.expireSec > 0 && proposal.expireSec < nowSec;
                  if (isExpired && !proposal.resolved) {
                    const totalIn = proposal.total_collateral_in || 0;
                    const qYes = proposal.q_yes || 0;
                    const qNo = proposal.q_no || 0;
                    if (totalIn === 0) {
                      return (
                        <span
                          style={{
                            padding: "2px 8px",
                            borderRadius: "12px",
                            fontSize: "0.75em",
                            backgroundColor: "rgba(239, 68, 68, 0.2)",
                            color: "#ef4444",
                            fontWeight: 600,
                          }}
                        >
                          No Trading
                        </span>
                      );
                    }
                    if (
                      (proposal.outcomes_count || 2) === 2 &&
                      (qYes === 0 || qNo === 0)
                    ) {
                      return (
                        <span
                          style={{
                            padding: "2px 8px",
                            borderRadius: "12px",
                            fontSize: "0.75em",
                            backgroundColor: "rgba(245, 158, 11, 0.2)",
                            color: "#f59e0b",
                            fontWeight: 600,
                          }}
                        >
                          One-Sided Trading
                        </span>
                      );
                    }
                  }
                  return null;
                })()}
                <span className="community-vote-count">
                  {proposal.approvalCount} votes
                </span>
              </div>
              <div className="community-proposal-body">
                {proposal.image_url && (
                  <div className="community-proposal-thumbnail">
                    <img src={proposal.image_url} alt="Market" />
                  </div>
                )}
                <div className="community-proposal-content">
                  <h4 className="community-proposal-question">
                    {proposal.question}
                  </h4>
                  <div className="community-proposal-details">
                    <p>
                      <strong>Category:</strong> {proposal.category}
                    </p>
                    <p>
                      <strong>Expires:</strong>{" "}
                      {formatDate(proposal.expireSec, true)}
                    </p>
                    {proposal.outcomes && proposal.outcomes.length > 0 && (
                      <p>
                        <strong>Outcomes:</strong>{" "}
                        {proposal.outcomes.join(", ")}
                      </p>
                    )}
                    <p>
                      <strong>Proposed by:</strong> {proposal.suggested_by}
                    </p>
                  </div>
                </div>
              </div>

              <div className="community-proposal-vote-section">
                {(() => {
                  const nowSec = Math.floor(Date.now() / 1000);
                  const isExpired =
                    proposal.expireSec > 0 && proposal.expireSec < nowSec;
                  const totalIn = proposal.total_collateral_in || 0;
                  const qYes = proposal.q_yes || 0;
                  const qNo = proposal.q_no || 0;
                  const isIlliquid =
                    isExpired &&
                    !proposal.resolved &&
                    (totalIn === 0 ||
                      ((proposal.outcomes_count || 2) === 2 &&
                        (qYes === 0 || qNo === 0)));
                  if (isIlliquid) {
                    return (
                      <div
                        className="community-vote-status own-market"
                        style={{ color: "#f59e0b" }}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          width="16"
                          height="16"
                        >
                          <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                          <path d="M12 9v4M12 17h.01" />
                        </svg>
                        Voting disabled for illiquid markets
                      </div>
                    );
                  }
                  return null;
                })() ||
                  (!session ? (
                    <div className="community-vote-status own-market">
                      Connect your wallet to vote
                    </div>
                  ) : proposal.userHasApproved ? (
                    <div className="community-vote-status approved">
                      Awesome! You have already voted for this market.
                    </div>
                  ) : proposal.suggested_by === currentUser ? (
                    <div className="community-vote-status own-market">
                      You cannot vote on your own proposal
                    </div>
                  ) : (
                    <button
                      className="community-approve-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleApprove(proposal.id);
                      }}
                      disabled={approveLoadingId === proposal.id}
                    >
                      {approveLoadingId === proposal.id ? (
                        "Voting..."
                      ) : (
                        <>
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            width="16"
                            height="16"
                          >
                            <path d="M20 6L9 17l-5-5" />
                          </svg>
                          Vote
                        </>
                      )}
                    </button>
                  ))}
                  {
                    !proposal.userHasApproved && proposal.suggested_by !== currentUser && (
                      <div className="community-vote-status-note">
                        Show your support for this market by voting.Your vote can help bring this market live.
                      </div>
                    )
                  }
              </div>

              {/* Recent comments preview */}
              {(() => {
                const marketComments = (cardComments[proposal.id] || []).filter(
                  (c) => !c.is_deleted,
                );
                const totalCount = marketComments.length;
                const recentComments = marketComments
                  .sort((a, b) => b.created_at - a.created_at)
                  .slice(0, 3);
                return (
                  <div
                    className="community-card-comments"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="community-card-comments-header">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        width="14"
                        height="14"
                      >
                        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                      </svg>
                      <span>
                        {totalCount} comment{totalCount !== 1 ? "s" : ""}
                      </span>
                    </div>
                    {recentComments.length > 0 ? (
                      <>
                        {recentComments.map((c) => (
                          <div
                            key={c.id}
                            className="community-card-comment-preview"
                          >
                            <span className="community-card-comment-author">
                              {c.user_account}
                            </span>
                            <span className="community-card-comment-text">
                              {c.comment_text.length > 80
                                ? c.comment_text.substring(0, 80) + "..."
                                : c.comment_text}
                            </span>
                          </div>
                        ))}
                        {totalCount > 3 && (
                          <button
                            className="community-card-view-all-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewDetail(proposal.id);
                            }}
                          >
                            View all {totalCount} comments
                          </button>
                        )}
                      </>
                    ) : (
                      <p className="community-card-no-comments">
                        No comments yet
                      </p>
                    )}
                  </div>
                );
              })()}

              {/* Decline record on card */}
              {declineRecords[proposal.id] && (
                <div
                  style={{
                    background: "rgba(239, 68, 68, 0.08)",
                    border: "1px solid rgba(239, 68, 68, 0.3)",
                    borderRadius: "6px",
                    padding: "8px 12px",
                    margin: "8px 0 0 0",
                  }}
                >
                  <p
                    style={{
                      color: "#ef4444",
                      margin: "0 0 4px 0",
                      fontSize: "12px",
                      fontWeight: 600,
                    }}
                  >
                    Declined by Super Admin
                  </p>
                  <p
                    style={{ color: "#e2e8f0", margin: "0", fontSize: "12px" }}
                  >
                    {declineRecords[proposal.id].decline_comment.length > 100
                      ? declineRecords[proposal.id].decline_comment.substring(
                          0,
                          100,
                        ) + "..."
                      : declineRecords[proposal.id].decline_comment}
                  </p>
                </div>
              )}

              <div className="community-view-details-hint">
                Click to view details
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  width="14"
                  height="14"
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Toast notification */}
      {toast && (
        <div className={`toast-notification ${toast.type}`}>
          <span className="toast-message">{toast.message}</span>
          <button className="toast-close" onClick={() => setToast(null)}>
            &times;
          </button>
        </div>
      )}
    </div>
  );
};

export default UpcomingMarkets;
